import { useCallback, useEffect, useState } from 'react'
import { reportClientError } from 'utils/reportClientError'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { logMessage } from 'utils/logMessage'
import {
  useClassicStakeContract,
  useClassicDoubleStakeContract,
  useClassicStakeWriteContract,
  useClassicDoubleStakeWriteContract
} from 'hooks/useContract'
import { MproStake, MPROStake } from 'config/abi/types/MproStake'
import { BigNumber, ContractTransaction } from 'ethers'
import type { MetaToken } from 'types/metaToken'
import { resolveClassicStakeTokenDetails } from 'utils/classicStakeResolveTokens'
import { useGetMproPrice } from 'hooks/useGetMproPrice'
import { BNToNumberWithDecimals, stringToBigNumber } from '@mprolab/sdk'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { StakeProgress, stakeStore } from 'mobxStores/StakeStore'
import { getMulticallResponse } from 'utils/getMulticallResponse'
import { useGetTokenPrice } from 'hooks/useGetTokenPrice'
import { Web3Provider, JsonRpcSigner } from '@ethersproject/providers'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { MproDoubleStake } from 'config/abi/types'

dayjs.extend(isBetween)

const SECONDS_IN_YEAR = 365 * 24 * 60 * 60

export enum StakeStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive'
}

export type StakeVersion = 'v1' | 'v2'

export interface ClassicStakeConfig extends MPROStake.StakeConfigStructOutput {
  chainId: number
  contractAddress: string
  rewardToken: MetaToken
  stakeToken: MetaToken
  stakeStatus: StakeStatus
  apr: number
  version: StakeVersion
  apy?: number
  fee?: number
  burnFee?: number
  unstakeStartTimestamp?: BigNumber
  /**
   * What this wallet can withdraw right now, and when the next tranche
   * unlocks. These stakes vest the principal — a percentage per period — and
   * neither number appeared anywhere in the interface, so the schedule was
   * discoverable only by attempting a withdrawal and failing.
   */
  releasableNow?: BigNumber
  nextReleaseAt?: BigNumber
  claimRewardStartTimestamp?: BigNumber
  updateStakersEndTimestamp?: BigNumber
  token0?: MetaToken
  token1?: MetaToken
  stakeTokenPrice?: number
  rewardTokenPrice?: number
  stakeTokenAddress?: string
  rewardTokenAddress?: string
}

export interface ClassicStakeUserConfig {
  staked: BigNumber
  reward: BigNumber
  // TODO: Uncomment when needed
  // balanceWithRewards: BigNumber
  // claimedBalance: BigNumber
  rewardDebt: BigNumber
  pendingReward: BigNumber
}

export interface AggregatedClassicStakeConfig {
  stake: ClassicStakeConfig
  userStakeData?: ClassicStakeUserConfig
}

/**
 * A signer, obtained from the wallet at the moment it is needed.
 *
 * `account` and `provider` do not arrive together. `useAccount()` gives the
 * address synchronously, while the provider is set by an async effect that
 * awaits `connector.getProvider()` — so there is a window where the app knows
 * who you are and has nothing that can sign. Every write path assumed the two
 * were simultaneous.
 *
 * The first attempt at this fixed nothing, and the reason is worth keeping:
 * `getSigner` is written `library?.getSigner(account).connectUnchecked()`, so
 * a missing provider makes the whole expression `undefined` rather than
 * throwing — and a helper that re-attached "the signer" was re-attaching
 * nothing. Three wallets hit `sending a transaction requires a signer` after
 * that release, which is what proved it.
 *
 * So this asks the connector directly, which is always current, and returns
 * undefined only when the wallet genuinely cannot sign. Callers must refuse to
 * send in that case rather than letting ethers explain it.
 */
async function resolveWalletSigner(
  provider: Web3Provider | undefined,
  connector: unknown,
  account: string | undefined
): Promise<JsonRpcSigner | undefined> {
  if (!account) return undefined
  try {
    if (provider) return provider.getSigner(account)
  } catch {
    // fall through to the connector
  }
  try {
    const raw = await (connector as { getProvider?: () => Promise<unknown> })?.getProvider?.()
    if (raw) return new Web3Provider(raw as never, 'any').getSigner(account)
  } catch {
    // nothing to sign with
  }
  return undefined
}

/** Thrown when the wallet cannot sign, so the decoder can say so plainly. */
function walletNotReadyError(): Error {
  return new Error('The wallet requires a signer and none is attached yet')
}

const isDualStake = (stakeContract: MproStake | MproDoubleStake): stakeContract is MproDoubleStake => {
  return !(typeof (stakeContract as any).unstake === 'function')
}

/**
 * A stake transaction that failed, sent where somebody can see it.
 *
 * Until this existed the entire record of a failure was a red badge on the
 * user's screen. A person reported "Transaction failed — something went wrong"
 * on an unstake and there was nothing to look at: no wallet, no message, no
 * reason. The stakes hold the largest unclaimed balances on the platform, so
 * this is the page where being blind costs the most.
 *
 * `handled` says whether we had a specific sentence for it. A rising count of
 * unhandled failures is the queue of messages still worth writing.
 */
const KNOWN_STAKE_FAILURES = [
  'MPROStake: Unstake period has not started',
  'Stake period has ended',
  'MPROStake: No tokens to claim',
  'MPRODoubleRewardStake: No tokens to claim',
  'MPROStake: Compound is not enabled',
  'MPROStake: No rewards to compound',
  'Pausable: paused',
  'does not support programmatic chain switching',
  'insufficient funds for',
  'Staker is not whitelisted'
]

function reportStakeFailure(action: string, stakeAddress: string, wallet: string | undefined, error: any): void {
  const message = String(error?.message ?? error?.reason ?? error ?? '')
  /*
   * Declining in the wallet is a decision, not a fault worth an alert — in
   * every shape it arrives in. Filtering on the code alone let
   * "User rejected the request." through under a different code, and it sat in
   * the unhandled queue pretending to be a defect. That queue is only useful
   * while everything in it is something we could have said better.
   */
  if (
    error?.code === 'ACTION_REJECTED' ||
    error?.code === 4001 ||
    /user rejected|user denied|rejected by the user/i.test(message)
  )
    return
  reportClientError({
    kind: 'transaction',
    message: `stake:${action} ${stakeAddress} — ${message}`.slice(0, 500),
    wallet,
    handled: KNOWN_STAKE_FAILURES.some((known) => message.includes(known))
  })
}

export const useClassicStake = (): {
  activeStakes: AggregatedClassicStakeConfig[]
  inactiveStakes: AggregatedClassicStakeConfig[]
  isLoading: boolean
  fetchClassicStakeConfig: (userAccount?: string, refetch?: boolean) => Promise<void>
  stake: (stakeAddress: string, amount: BigNumber) => Promise<void>
  unstake: (stakeAddress: string, amount: BigNumber) => Promise<void>
  claimReward: (stakeAddress: string) => Promise<void>
  getEnableUnstakeForReleaseValue: (stakeAddress: string) => Promise<BigNumber>
  getReleaseInfo: (stakeAddress: string) => Promise<{ releasable: BigNumber; nextReleaseAt: number | null }>
} => {
  const [isLoading, setIsLoading] = useState(true)
  const [activeStakes, setActiveStakes] = useState<AggregatedClassicStakeConfig[]>([])
  const [inactiveStakes, setInactiveStakes] = useState<AggregatedClassicStakeConfig[]>([])
  const stakeContracts = useClassicStakeContract()
  const stakeDualStakeContracts = useClassicDoubleStakeContract()
  const stakeWriteContracts = useClassicStakeWriteContract()
  const stakeDualWriteContracts = useClassicDoubleStakeWriteContract()
  const allStakeAddresses = [...stakeContracts, ...stakeDualStakeContracts]
  const allStakeWriteContracts = [...stakeWriteContracts, ...stakeDualWriteContracts]
  const { getMproPrice } = useGetMproPrice()
  const { getTokenPrice } = useGetTokenPrice()
  const { account, provider, chainId, connector } = useWeb3React()
  // Used by getReleaseInfo for reads; writes resolve their own signer.
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()

  const fetchClassicStakeConfig = async (userAccount?: string, refetch?: boolean) => {
    try {
      if (!refetch) setIsLoading(true)
      const results: AggregatedClassicStakeConfig[] = await Promise.all(
        stakeContracts.map(async (stake) => {
          try {
            const stakeNetwork = await stake.provider.getNetwork()
            const [
              stakeConfigRes,
              treasuryFeeRes,
              burnFeeRes,
              stakeTokenRes,
              rewardTokenRes,
              claimRewardStartTimestampRes,
              nextUnstakeReleaseTimestampRes,
              compoundEnabledRes
            ] = await getMulticallResponse({
              chainId: stakeNetwork.chainId,
              calls: [
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('getStakeConfig'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('divPoolFee'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('divBurnFee'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('stakeToken'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('rewardToken'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('claimRewardStartTimestamp'),
                  allowFailure: true
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('unstakeStartTimestamp'),
                  allowFailure: true
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('compoundEnabled'),
                  allowFailure: false
                }
              ]
            })

            const [stakeConfig] = stake.interface.decodeFunctionResult('getStakeConfig', stakeConfigRes.returnData)
            const [treasuryFee] = stake.interface.decodeFunctionResult('divPoolFee', treasuryFeeRes.returnData)
            const [burnFee] = stake.interface.decodeFunctionResult('divBurnFee', burnFeeRes.returnData)
            const [stakeToken] = stake.interface.decodeFunctionResult('stakeToken', stakeTokenRes.returnData)
            const [rewardToken] = stake.interface.decodeFunctionResult('rewardToken', rewardTokenRes.returnData)
            const [claimRewardStartTimestamp] = stake.interface.decodeFunctionResult(
              'claimRewardStartTimestamp',
              claimRewardStartTimestampRes.returnData
            )
            const [unstakeStartTimestamp] = stake.interface.decodeFunctionResult(
              'unstakeStartTimestamp',
              nextUnstakeReleaseTimestampRes.returnData
            )
            const [compoundEnabled] = stake.interface.decodeFunctionResult(
              'compoundEnabled',
              compoundEnabledRes.returnData
            )

            const { stakeTokenMeta, rewardTokenMeta, token0, token1, actualStakeTokenPrice, actualRewardTokenPrice } =
              await resolveClassicStakeTokenDetails(
                stakeToken,
                rewardToken,
                stakeNetwork.chainId,
                getMproPrice,
                getTokenPrice
              )

            const isActive =
              dayjs().isBetween(
                dayjs.unix(Number(stakeConfig.stakeStartTimestamp)),
                dayjs.unix(Number(stakeConfig.stakeEndTimestamp))
              ) || dayjs().isBefore(dayjs.unix(Number(stakeConfig.stakeStartTimestamp)))

            // TODO: For now we will use only BASE_CHAIN_ID_NUMBER for price fetching due to MPRO price fluctuations on ETH

            const actualStakeTokenPriceBigNumber = stringToBigNumber(actualStakeTokenPrice?.toString() || '')
            const actualRewardTokenPriceBigNumber = stringToBigNumber(actualRewardTokenPrice?.toString() || '')

            const rewardPerYearValue = stakeConfig.rewardPerSecond
              .mul(BigNumber.from(SECONDS_IN_YEAR))
              .mul(actualRewardTokenPriceBigNumber)
              .div(BigNumber.from(10).pow(18))

            const totalStakedSupplyValue = stakeConfig.totalStakedSupply
              .mul(actualStakeTokenPriceBigNumber)
              .div(BigNumber.from(10).pow(18))

            let apr = BigNumber.from(0)
            if (totalStakedSupplyValue.gt(BigNumber.from(0)))
              apr = BigNumber.from(
                (
                  (BNToNumberWithDecimals(rewardPerYearValue) / BNToNumberWithDecimals(totalStakedSupplyValue)) *
                  100
                ).toFixed()
              )

            let userStakeData: ClassicStakeUserConfig

            if (userAccount) {
              const [userDataRes, userPendingRewardRes] = await getMulticallResponse({
                chainId: stakeNetwork.chainId,
                calls: [
                  {
                    target: stake.address,
                    callData: stake.interface.encodeFunctionData('staker', [userAccount]),
                    allowFailure: false
                  },
                  {
                    target: stake.address,
                    callData: stake.interface.encodeFunctionData('stakerPendingReward', [userAccount]),
                    allowFailure: false
                  }
                ]
              })

              const userData = stake.interface.decodeFunctionResult('staker', userDataRes.returnData)
              const [userPendingReward] = stake.interface.decodeFunctionResult(
                'stakerPendingReward',
                userPendingRewardRes.returnData
              )

              userStakeData = {
                staked: userData.staked,
                reward: userData.reward,
                rewardDebt: userData.rewardDebt,
                pendingReward: userPendingReward
              }
            }

            return {
              stake: {
                chainId: stakeNetwork.chainId,
                contractAddress: stake.address,
                ...stakeConfig,
                stakeToken: stakeTokenMeta,
                rewardToken: rewardTokenMeta,
                token0,
                token1,
                stakeStatus: isActive ? StakeStatus.ACTIVE : StakeStatus.INACTIVE,
                apr: Number(apr?.toString()),
                apy: compoundEnabled ? Number(apr?.toString()) : 0,
                fee: treasuryFee.toNumber() / 100,
                burnFee: burnFee.toNumber() / 100,
                unstakeStartTimestamp: unstakeStartTimestamp || 0,
                claimRewardStartTimestamp: claimRewardStartTimestamp || 0,
                stakeTokenPrice: actualStakeTokenPrice,
                rewardTokenPrice: actualRewardTokenPrice,
                stakeTokenAddress: stakeToken,
                rewardTokenAddress: rewardToken,
                version: 'v1'
              } as ClassicStakeConfig,
              userStakeData
            }
          } catch (error) {
            logMessage(error, 'fetchClassicStakeConfig')
            return null
          }
        })
      )
      const dualStakeResults: AggregatedClassicStakeConfig[] = await Promise.all(
        stakeDualStakeContracts.map(async (stake) => {
          try {
            const stakeNetwork = await stake.provider.getNetwork()
            const [
              stakeConfigRes,
              stakeTokenRes,
              rewardTokenRes,
              claimStakedStartTimestampRes,
              updateStakersEndTimestampRes,
              nextReleaseTimestampRes
            ] = await getMulticallResponse({
              chainId: stakeNetwork.chainId,
              calls: [
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('getStakeConfig'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('stakeToken'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('rewardToken'),
                  allowFailure: false
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('claimStakedStartTimestamp'),
                  allowFailure: true
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('updateStakersEndTimestamp'),
                  allowFailure: true
                },
                {
                  target: stake.address,
                  callData: stake.interface.encodeFunctionData('nextReleaseTimestamp'),
                  allowFailure: true
                }
              ]
            })

            const [stakeConfig] = stake.interface.decodeFunctionResult('getStakeConfig', stakeConfigRes.returnData)
            const [stakeToken] = stake.interface.decodeFunctionResult('stakeToken', stakeTokenRes.returnData)
            const [rewardToken] = stake.interface.decodeFunctionResult('rewardToken', rewardTokenRes.returnData)
            /*
             * When unstaking is genuinely closed for THIS wallet.
             *
             * These contracts release in periods and expose `nextReleaseTimestamp`,
             * but that is always in the future — it is the *next* one. Mapping it
             * straight onto the countdown would disable the button permanently for
             * everybody, which is worse than the problem it fixes. What decides is
             * `enableForRelease`, and that is read **per caller**: without a `from`
             * the contract answers for address zero, which is always nothing.
             *
             * So the countdown appears only when this wallet has nothing available.
             * An unreadable value leaves the button alone — a failed read must never
             * stand between somebody and their own tokens.
             */
            let releaseBlockedUntil: BigNumber | undefined
            let releasableNow: BigNumber | undefined
            let nextReleaseAt: BigNumber | undefined
            try {
              const [nextRelease] = stake.interface.decodeFunctionResult(
                'nextReleaseTimestamp',
                nextReleaseTimestampRes.returnData
              )
              if (nextRelease?.gt(0)) nextReleaseAt = nextRelease
              if (userAccount) {
                releasableNow = await stake.enableForRelease({ from: userAccount })
                if (nextRelease?.gt(0) && releasableNow?.isZero()) releaseBlockedUntil = nextRelease
              }
            } catch {
              // Not exposed, or unreadable: no countdown, button unchanged.
            }

            const [claimStakedStartTimestamp] = stake.interface.decodeFunctionResult(
              'claimStakedStartTimestamp',
              claimStakedStartTimestampRes.returnData
            )
            const [updateStakersEndTimestamp] = stake.interface.decodeFunctionResult(
              'updateStakersEndTimestamp',
              updateStakersEndTimestampRes.returnData
            )

            const { stakeTokenMeta, rewardTokenMeta, token0, token1, actualStakeTokenPrice, actualRewardTokenPrice } =
              await resolveClassicStakeTokenDetails(
                stakeToken,
                rewardToken,
                stakeNetwork.chainId,
                getMproPrice,
                getTokenPrice
              )

            const isActive =
              dayjs().isBetween(
                dayjs.unix(Number(stakeConfig.stakeStartTimestamp)),
                dayjs.unix(Number(stakeConfig.stakeEndTimestamp))
              ) || dayjs().isBefore(dayjs.unix(Number(stakeConfig.stakeStartTimestamp)))

            // TODO: For now we will use only BASE_CHAIN_ID_NUMBER for price fetching due to MPRO price fluctuations on ETH

            const actualStakeTokenPriceBigNumber = stringToBigNumber(actualStakeTokenPrice?.toString() || '')
            const actualRewardTokenPriceBigNumber = stringToBigNumber(actualRewardTokenPrice?.toString() || '')

            const rewardPerYearValue = stakeConfig.rewardPerSecond
              .mul(BigNumber.from(SECONDS_IN_YEAR))
              .mul(actualRewardTokenPriceBigNumber)
              .div(BigNumber.from(10).pow(18))

            const totalStakedSupplyValue = stakeConfig.totalStakedSupply
              .mul(actualStakeTokenPriceBigNumber)
              .div(BigNumber.from(10).pow(18))

            let apr = BigNumber.from(0)
            if (totalStakedSupplyValue.gt(BigNumber.from(0)))
              apr = BigNumber.from(
                (
                  (BNToNumberWithDecimals(rewardPerYearValue) / BNToNumberWithDecimals(totalStakedSupplyValue)) *
                  100
                ).toFixed()
              )

            let userStakeData: ClassicStakeUserConfig

            if (userAccount) {
              const [userDataRes] = await getMulticallResponse({
                chainId: stakeNetwork.chainId,
                calls: [
                  {
                    target: stake.address,
                    callData: stake.interface.encodeFunctionData('staker', [userAccount]),
                    allowFailure: false
                  }
                ]
              })
              const userData = stake.interface.decodeFunctionResult('staker', userDataRes.returnData)
              const userPendingReward = await stake.getRewardAmount()

              userStakeData = {
                staked: userData.staked.sub(userData.claimedStaked),
                reward: userData.reward,
                rewardDebt: userData.rewardDebt,
                pendingReward: userPendingReward
              }
            }

            return {
              stake: {
                chainId: stakeNetwork.chainId,
                contractAddress: stake.address,
                ...stakeConfig,
                stakeToken: stakeTokenMeta,
                rewardToken: rewardTokenMeta,
                token0,
                token1,
                stakeStatus: isActive ? StakeStatus.ACTIVE : StakeStatus.INACTIVE,
                apr: Number(apr?.toString()),
                apy: Number(apr?.toString()),
                claimRewardStartTimestamp: claimStakedStartTimestamp || 0,
                // The stake table's UNSTAKE_COUNTER keys off this field. It was
                // never set for double-reward stakes — which is why their Unstake
                // button always looked ready, whatever the release schedule said.
                unstakeStartTimestamp: releaseBlockedUntil,
                releasableNow,
                nextReleaseAt,
                updateStakersEndTimestamp: updateStakersEndTimestamp || 0,
                stakeTokenPrice: actualStakeTokenPrice,
                rewardTokenPrice: actualRewardTokenPrice,
                stakeTokenAddress: stakeToken,
                rewardTokenAddress: rewardToken,
                version: 'v2'
              } as ClassicStakeConfig,
              userStakeData
            }
          } catch (error) {
            logMessage(error, 'fetchClassicStakeConfig')
            return null
          }
        })
      )
      const stakes = [...results, ...dualStakeResults]

      const active = stakes.filter((singleStake) => singleStake?.stake.stakeStatus === StakeStatus.ACTIVE)
      const inactive = stakes.filter((singleStake) => singleStake?.stake.stakeStatus === StakeStatus.INACTIVE)

      setActiveStakes(active)
      setInactiveStakes(inactive)
      if (!refetch) setIsLoading(false)
    } catch (error) {
      logMessage(`Error fetching data:${error}`)
      setIsLoading(false)
    }
  }

  const stake = useCallback(
    async (stakeAddress: string, amount: BigNumber) => {
      try {
        const found = allStakeWriteContracts.find((contract) => contract.address === stakeAddress)
        const signer = await resolveWalletSigner(provider, connector, account)
        if (!signer) throw walletNotReadyError()
        const stakeContract = found.connect(signer)
        const tx = await stakeContract.stake(amount)
        stakeStore.setStakeProgress(StakeProgress.PENDING)
        await tx.wait()
        stakeStore.setTxHash(tx.hash)
        stakeStore.setStakeProgress(StakeProgress.SUCCESS)
      } catch (error: any) {
        stakeStore.setError(error)
        reportStakeFailure('stake', stakeAddress, account, error)
        if (error.code === 'ACTION_REJECTED') {
          stakeStore.setStakeProgress(StakeProgress.REJECTED)
        } else {
          stakeStore.setStakeProgress(StakeProgress.ERROR)
        }
      }
    },
    [account, chainId, connector, provider, allStakeWriteContracts]
  )

  const unstake = useCallback(
    async (stakeAddress: string, amount: BigNumber) => {
      try {
        const found = allStakeWriteContracts.find((contract) => contract.address === stakeAddress)
        const signer = await resolveWalletSigner(provider, connector, account)
        if (!signer) throw walletNotReadyError()
        const stakeContract = found.connect(signer)
        let tx: ContractTransaction
        if (isDualStake(stakeContract)) {
          tx = await stakeContract.claim()
        } else {
          tx = await stakeContract.unstake(amount)
        }
        stakeStore.setStakeProgress(StakeProgress.PENDING)
        await tx.wait()
        stakeStore.setTxHash(tx.hash)
        stakeStore.setStakeProgress(StakeProgress.SUCCESS)
      } catch (error: any) {
        stakeStore.setError(error)
        reportStakeFailure('unstake', stakeAddress, account, error)
        if (error.code === 'ACTION_REJECTED') {
          stakeStore.setStakeProgress(StakeProgress.REJECTED)
        } else {
          stakeStore.setStakeProgress(StakeProgress.ERROR)
        }
      }
    },
    [account, chainId, connector, allStakeWriteContracts, provider]
  )

  const claimReward = useCallback(
    async (stakeAddress: string) => {
      try {
        const found = allStakeWriteContracts.find((contract) => contract.address === stakeAddress)
        const signer = await resolveWalletSigner(provider, connector, account)
        if (!signer) throw walletNotReadyError()
        const stakeContract = found.connect(signer)
        const tx = await stakeContract.claimReward()
        stakeStore.setStakeProgress(StakeProgress.PENDING)
        await tx.wait()
        stakeStore.setTxHash(tx.hash)
        stakeStore.setStakeProgress(StakeProgress.SUCCESS)
      } catch (error: any) {
        stakeStore.setError(error)
        reportStakeFailure('claimReward', stakeAddress, account, error)
        if (error.code === 'ACTION_REJECTED') {
          stakeStore.setStakeProgress(StakeProgress.REJECTED)
        } else {
          stakeStore.setStakeProgress(StakeProgress.ERROR)
        }
      }
    },
    [account, chainId, connector, allStakeWriteContracts, provider]
  )

  /**
   * What this wallet can actually take out right now, and when the next
   * tranche opens.
   *
   * These stakes release in periods — `claimStakedPeriodDuration` on the
   * double-reward contracts is a day — so between releases there is genuinely
   * nothing to claim and `claim()` reverts. Asking first is the difference
   * between telling somebody "the next release is tomorrow at 10:00" and
   * firing a transaction that cannot succeed and calling the result
   * "Something went wrong, try again in a while".
   *
   * `enableForRelease` is read **per caller**, so the `from` matters: without
   * it the contract answers for address zero, which is always nothing.
   */
  const getReleaseInfo = useCallback(
    async (stakeAddress: string): Promise<{ releasable: BigNumber; nextReleaseAt: number | null }> => {
      const stakeContract = allStakeWriteContracts.find((contract) => contract.address === stakeAddress)
      let releasable = BigNumber.from(0)
      let nextReleaseAt: number | null = null
      try {
        releasable = isDualStake(stakeContract)
          ? await stakeContract.enableForRelease({ from: account })
          : await stakeContract.enableUnstakeForRelease({ from: account })
      } catch {
        // Unreadable is not zero: leave it and let the caller proceed rather
        // than block somebody out of their own tokens on a failed read.
        return { releasable: BigNumber.from(-1), nextReleaseAt: null }
      }
      try {
        const next = await (stakeContract as any).nextReleaseTimestamp()
        const asNumber = Number(next)
        if (Number.isFinite(asNumber) && asNumber > 0) nextReleaseAt = asNumber
      } catch {
        // Not every stake exposes it; the message simply omits the date.
      }
      return { releasable, nextReleaseAt }
    },
    [account, allStakeWriteContracts]
  )

  const getEnableUnstakeForReleaseValue = useCallback(
    async (stakeAddress: string): Promise<BigNumber> => {
      const stakeContract = allStakeWriteContracts.find((contract) => contract.address === stakeAddress)
      let enableUnstakeForReleaseValue: BigNumber
      if (isDualStake(stakeContract)) {
        enableUnstakeForReleaseValue = await stakeContract.enableForRelease({
          from: account
        })
      } else {
        enableUnstakeForReleaseValue = await stakeContract.enableUnstakeForRelease({
          from: account
        })
      }
      return enableUnstakeForReleaseValue
    },
    [account, chainId, connector, allStakeWriteContracts, provider]
  )

  return {
    activeStakes,
    inactiveStakes,
    isLoading,
    fetchClassicStakeConfig,
    stake,
    unstake,
    claimReward,
    getEnableUnstakeForReleaseValue,
    getReleaseInfo
  }
}

export enum StakeCounterType {
  STARTS,
  ACTIVE,
  ENDED
}

export interface StakeCounterProps {
  totalTime: number
  progress: number
  counterLoading: boolean
  stakeInProgress: boolean
  eventType: StakeCounterType
}

export const useStakeTimestampCounter = (config: ClassicStakeConfig): StakeCounterProps => {
  const [totalTime, setTotalTime] = useState<number>(0)
  const [progress, setProgress] = useState<number>(0)
  const [counterLoading, setCounterLoading] = useState<boolean>(true)
  const [stakeInProgress, setStakeInProgress] = useState<boolean>(false)
  const [eventType, setEventType] = useState<StakeCounterType>(StakeCounterType.STARTS)

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    const updateTimestamp = async () => {
      try {
        const endDate = dayjs.unix(Number(config?.stakeEndTimestamp))
        const startDate = dayjs.unix(Number(config?.stakeStartTimestamp))
        const currentDate = dayjs()

        const totalMilliseconds = endDate.diff(startDate, 'millisecond')

        switch (true) {
          case currentDate.isBefore(startDate):
            setEventType(StakeCounterType.STARTS)
            setStakeInProgress(false)
            const millisecondsUntilStart = startDate.diff(currentDate, 'millisecond')
            setTotalTime(millisecondsUntilStart)
            break
          case currentDate.isBetween(startDate, endDate, null, '[]'):
            setEventType(StakeCounterType.ACTIVE)
            setStakeInProgress(true)
            const millisecondsLeft = endDate.diff(currentDate, 'millisecond')
            setTotalTime(millisecondsLeft)
            setProgress((millisecondsLeft / totalMilliseconds) * 100)
            break
          default:
            setEventType(StakeCounterType.ENDED)
            setStakeInProgress(false)
            setTotalTime(0)
            setProgress(100)
            setCounterLoading(false)
            return
        }

        intervalId = setInterval(() => {
          setTotalTime((prevTime) => {
            const newTime = prevTime - 60000 // Decrement by one minute each interval
            if (newTime <= 0) {
              clearInterval(intervalId)
              return 0 // Prevent negative values
            }

            // Update progress
            const newProgress = (newTime / totalMilliseconds) * 100
            setProgress(newProgress)
            return newTime
          })
        }, 60000)
      } catch (error) {
        console.error(`Failed to fetch timestamp: ${error}`)
      } finally {
        setCounterLoading(false)
      }
    }

    updateTimestamp()

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [config])

  return { totalTime, progress, counterLoading, stakeInProgress, eventType }
}
